import { Fragment } from 'react';
import { useLocation } from 'react-router';
import { useSocialLinks } from './socialLinks';

// Brand suffix only — deliberately NOT 'SieveGuard — Email Integrity API'. Google renders about
// 60 chars of <title>; the long form spent 35 of them on the same string for all 37 pages, which
// truncated the part that actually differentiates each result. "Email integrity API" still earns
// its keyword weight where it belongs: the homepage H1 + description + SoftwareApplication schema.
const SITE = 'SieveGuard';
/**
 * Canonical one-line product description — the single source of truth for how SieveGuard is
 * described. Reused as the default meta description AND the homepage SoftwareApplication
 * description so the positioning never drifts across the copies that used to be hand-maintained
 * (an LLM that sees three slightly-different summaries trusts none of them). ≤160 chars for the
 * SERP/snippet budget.
 */
export const PRODUCT_DESCRIPTION =
  'SieveGuard clusters near-duplicate email addresses as they arrive, scores risk and verifies deliverability — catching bot and multi-accounting signups early.';
const DEFAULT_DESC = PRODUCT_DESCRIPTION;

interface SeoInput {
  /** Page title shown in browser tab + OG/Twitter cards. */
  title?: string;
  /** Meta description + OG/Twitter description (≤ 160 chars recommended). */
  description?: string;
  /** Path relative to origin, e.g. "/pricing". Used to build the canonical URL. */
  path?: string;
  /** OpenGraph image absolute URL (PNG, 1200×630). Defaults to /og-default.png. */
  image?: string;
  /** Mark crawler-discoverable (false → noindex,nofollow). Public pages = true. */
  index?: boolean;
  /**
   * Optional comma-separated keyword list. Modern crawlers largely ignore this but
   * Bing still gives it a slight signal + it's free; harmless inclusion.
   */
  keywords?: string;
  /**
   * Optional JSON-LD payloads to emit alongside the meta tags. Use the helpers
   * (organizationSchema / faqSchema / breadcrumbSchema / productSchema) to build
   * them in a strict, validate-able shape. Each emits as its own <script
   * type="application/ld+json"> so crawlers see them independently.
   */
  jsonLd?: object[];
}

/**
 * Patch <head> for SEO + social cards on a per-page basis. No library: we own the four
 * <meta> tags that matter (description, robots, og:*, twitter:*) plus <title> + canonical
 * link. Cleans up on unmount so the next route starts from a clean state.
 *
 * Vite SPA caveat: Google's crawler renders JS so this works, but if you want the absolute
 * best SEO for the marketing pages later, swap to vite-plugin-ssg / static prerender so
 * the meta tags ship in the initial HTML.
 */
/**
 * Canonical URL form = trailing slash, no query/fragment. nginx serves the marketing routes
 * at their trailing-slash form (`/pricing/`) and 301s the bare form, so the canonical, the
 * sitemap `<loc>`, and the prerender's baked canonical must ALL use the slash form — otherwise
 * the canonical names a URL that redirects away from the page declaring it (wasted crawl
 * budget, muddied consolidation). Root `/` stays as-is.
 */
/**
 * The canonical production origin — a CONSTANT on both the server render and the client.
 *
 * This used to be `typeof window === 'undefined' ? 'https://sieveguard.com' : window.location.origin`,
 * which was fine under react-helmet-async (it wrote the head imperatively, outside hydration). Once
 * React 19 started hydrating these tags as real DOM nodes it became a hydration mismatch: the
 * prerendered JSON-LD said `https://sieveguard.com` and the client recomputed a different origin on
 * any host that is not production, so React threw #418 and REGENERATED the tree on 33 of 38 routes.
 *
 * Pinning it is also the more correct SEO answer. A canonical, an og:url and a JSON-LD `@id` are
 * statements about where the page canonically LIVES, not about which host you happen to be viewing
 * it on — the old behaviour emitted `<link rel="canonical" href="http://localhost:5173/pricing/">`
 * in dev and would have emitted a staging hostname on any preview deploy.
 */
export const SITE_ORIGIN = 'https://sieveguard.com';

/**
 * Stable `@id` anchors so Organization / WebSite / SoftwareApplication link into ONE entity graph
 * across pages instead of appearing as disconnected islands. A fragment `@id` is a node IDENTITY,
 * not a URL a crawler fetches — so cross-page references (`publisher`, `isPartOf`, `about`) merge
 * onto the same entity, which is exactly what lets an LLM landing on a deep page know it belongs
 * to the same product it saw on the homepage. `@id` is exempt from the trailing-slash URL rule
 * (it is not a `url`/`item` value).
 */
export const ORG_ID = `${SITE_ORIGIN}/#organization`;
export const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
export const SOFTWARE_ID = `${SITE_ORIGIN}/#software`;

export function toTrailingSlash(p: string): string {
  const pathOnly = p.split(/[?#]/)[0] || '/';
  if (pathOnly === '/') return '/';
  return pathOnly.replace(/\/+$/, '') + '/';
}

/**
 * Client-only paths — rendered by the SPA shell, never prerendered as a directory.
 *
 * This distinction is the whole point of {@link publicHref}. nginx's `try_files $uri $uri/ …`
 * only 301s when a prerendered DIRECTORY exists, so `/pricing` → `/pricing/` but `/login` just
 * falls through to the shell at 200. Appending a slash to these would invent a URL form nothing
 * canonicalises to. Mirrors ALWAYS_DISALLOW in apps/api/src/platform/public-seo.controller.ts.
 */
const SPA_ONLY_PATH =
  /^\/(login|register|app|admin|oauth|affiliate|accept-invite|forgot-password|reset-password)(\/|$)/;

/**
 * Trailing-slash form of an INTERNAL href, for `<Link to>` on prerendered pages.
 *
 * Every public route is served at its trailing-slash form (nginx 301s the bare one), and the
 * canonical / sitemap / JSON-LD all use it — but `<Link to="/pricing">` emitted a bare href, so
 * ~2,000 internal links across the site pointed at a redirect. Harmless for PageRank (301s pass
 * it) but it wasted a hop per link and made the in-app URL disagree with the canonical one until
 * the next full page load.
 *
 * Normalise at RENDER time only: nav self-link filtering compares against the bare form, and the
 * SSG/hydration pair must agree on the href (see the React #418 note in PublicHeader).
 * Query strings and hashes are preserved — `/docs#bulk` → `/docs/#bulk`, never `/docs#bulk/`.
 */
export function publicHref(to: string): string {
  if (!to.startsWith('/') || SPA_ONLY_PATH.test(to)) return to;
  const [, path = '', suffix = ''] = to.match(/^([^?#]*)([?#].*)?$/) ?? [];
  if (path === '' || path === '/' || path.endsWith('/')) return to;
  return `${path}/${suffix}`;
}

/**
 * Per-page SEO head, hoisted into `<head>` by React 19 itself.
 *
 * Single source of truth: the page component that renders `<Seo>` is the ONLY place its
 * title/description/canonical/OG/JSON-LD come from — rendered into the static prerendered HTML
 * AND kept in sync on the client by the same component. This replaced the old split where
 * `scripts/public-routes.mjs` fed the static <head> while `useSeo` set the client <head>, so the
 * two drifted (different titles/descriptions on almost every route). There is now no prerender
 * head patch and no per-route metadata duplicated outside the page.
 *
 * WHY NOT `meta()`: framework mode's idiomatic answer is a `meta()` route export, but `meta()`
 * only sees loader data and params — it cannot call hooks. Several pages derive their title and
 * description from `useFeatures()` / legal-visibility flags, so `meta()` would force that state
 * through loaders on every public route. Rendering the tags in place keeps the SEO data exactly
 * where the component already computes it.
 *
 * WHY THIS WORKS WITHOUT HELMET: React 19 hoists `<title>`, `<meta>` and `<link>` rendered
 * ANYWHERE in the tree into `<head>`, on the server render and on the client, and dedupes them.
 * That is what `vite-react-ssg`'s `<Head>` (react-helmet-async) used to do for us; the platform
 * does it now, so both the wrapper and helmet are gone.
 *
 * JSON-LD stays in `<body>` where it renders — React only hoists scripts that are `async`, and
 * Google/Bing both read `application/ld+json` from the body. The SEO contract asserts on the
 * whole document, not on `<head>` specifically.
 */
export function Seo({
  title,
  description = DEFAULT_DESC,
  path,
  image,
  index = true,
  keywords,
  jsonLd,
}: SeoInput) {
  const fullTitle = title ? `${title} · ${SITE}` : SITE;
  // `useLocation()` rather than `window.location.pathname`: it resolves identically during
  // prerender and on the client, so an omitted `path` prop cannot desync the canonical.
  const { pathname } = useLocation();
  const origin = SITE_ORIGIN;
  const canonical = origin + toTrailingSlash(path ?? pathname);
  const ogImage = image ?? `${origin}/og-default.png`;
  // Every indexable page carries the Organization + WebSite identity (defined once, by @id) so an
  // LLM/crawler landing on ANY deep page — not just the homepage — can ground the page to the
  // product entity, with the real social profiles as sameAs. Seeded from the SSG snapshot (no
  // network at prerender). noindex pages get nothing (no entity to ground for a private route).
  const sameAs = useSocialLinks().map((s) => s.url);
  const graph = index !== false ? [...baseGraph(sameAs), ...(jsonLd ?? [])] : (jsonLd ?? []);

  return (
    <Fragment>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={index ? 'index,follow' : 'noindex,nofollow'} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      {/* Declared dimensions let Facebook/LinkedIn/Slack lay the card out on FIRST paste, before
          they have fetched the image — without them the first share of a URL often renders as a
          small thumbnail or no image at all, and the scraper caches that result. og-default.png
          is 1200×630 (the 1.91:1 large-card ratio); keep these in step if it is ever replaced. */}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content="SieveGuard" />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {/* All schemas go in ONE ld+json script as an array — Google parses top-level arrays,
          and react-helmet-async dedupes sibling <script>s that share the same attributes
          (which would otherwise drop all but one payload). */}
      {graph.length > 0 ? (
        <script type="application/ld+json">{JSON.stringify(graph)}</script>
      ) : null}
    </Fragment>
  );
}

// ── JSON-LD helpers ─────────────────────────────────────────────────────────
//
// Schema.org payloads that Google + Bing actually consume:
//
//   - Organization on the landing page → knowledge panel + sitelinks search box.
//   - Product on each feature page → can produce a rich card with name + price.
//   - FAQPage on any page with a FAQ block → Google shows the Q&A directly in
//     the SERP, dramatically boosting CTR.
//   - BreadcrumbList on every non-root page → breadcrumb trail in the SERP.
//
// Helpers stay tiny + composable so feature pages can stack as many as they need.

/** Top-level company knowledge graph. Emit once on the landing page. */
export function organizationSchema(opts: {
  name: string;
  url: string;
  logoUrl: string;
  sameAs?: string[];
  /** ISO 8601 (e.g. "2026-05") — a durable entity fact. */
  foundingDate?: string;
  /** ISO 3166-1 alpha-2 country only (no street) — a privacy-safe location signal for a sole trader. */
  addressCountry?: string;
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORG_ID,
    name: opts.name,
    url: opts.url,
    logo: opts.logoUrl,
    ...(opts.foundingDate ? { foundingDate: opts.foundingDate } : {}),
    // Country-only address — fixes the "no location" gap without publishing a home/street address.
    ...(opts.addressCountry
      ? { address: { '@type': 'PostalAddress', addressCountry: opts.addressCountry } }
      : {}),
    // sameAs (LinkedIn / X / GitHub / Crunchbase …) is the strongest entity-disambiguation signal
    // for LLMs, but it must be REAL — passed in only for profiles that exist, never fabricated.
    ...(opts.sameAs && opts.sameAs.length > 0 ? { sameAs: opts.sameAs } : {}),
    knowsAbout: ORG_KNOWS_ABOUT,
  };
}

/** Durable SieveGuard entity facts baked into the Organization node (not operator-config). */
const ORG_FOUNDING_DATE = '2026-05';
const ORG_COUNTRY = 'MT';

/**
 * Topical expertise, declared on the Organization entity.
 *
 * Entity clarity is what lets an AI-search system connect "who is authoritative on DKIM" to this
 * organisation rather than treating each page as an unattributed document. These are the subjects
 * the site demonstrably covers in depth — every one has at least one `/learn/` article and one
 * tool behind it. Do NOT pad the list with things we do not actually cover: an entity claiming
 * expertise its content does not support is the shape of the problem, not the fix.
 */
const ORG_KNOWS_ABOUT = [
  'Email authentication',
  'Sender Policy Framework (SPF)',
  'DomainKeys Identified Mail (DKIM)',
  'DMARC',
  'Email deliverability',
  'Sender reputation',
  'Email blacklists and DNSBLs',
  'Email list hygiene',
  'Signup fraud detection',
];

/**
 * Organization + WebSite, emitted on every indexable page by {@link Seo}. Defined once here with
 * the stable @ids so all the per-page nodes (SoftwareApplication.publisher, WebPage.isPartOf,
 * pricing Product.brand) reference ONE Organization and ONE WebSite rather than minting copies.
 * `sameAs` (the real social profiles) is threaded in by {@link Seo} from the live config.
 */
export function baseGraph(sameAs?: string[]): object[] {
  return [
    organizationSchema({
      name: SITE,
      url: `${SITE_ORIGIN}/`,
      logoUrl: `${SITE_ORIGIN}/favicon.svg`,
      foundingDate: ORG_FOUNDING_DATE,
      addressCountry: ORG_COUNTRY,
      sameAs,
    }),
    websiteSchema({ name: SITE, url: `${SITE_ORIGIN}/` }),
  ];
}

/** FAQ block schema — turns the existing FAQ <details> list into rich results. */
export function faqSchema(qa: Array<{ q: string; a: string }>): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qa.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

/** Breadcrumb trail — emit on any nested page. Crumbs in order, root first. */
export function breadcrumbSchema(crumbs: Array<{ name: string; url: string }>): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      // Normalised HERE, not at each call site. `item` must name the URL that actually serves
      // 200 — nginx 301s the bare form — and callers passing a bare URL had leaked a redirect
      // hop into the breadcrumb trail on 30+ pages. toTrailingSlash is safe on absolute URLs.
      item: toTrailingSlash(c.url),
    })),
  };
}

/**
 * BreadcrumbList from `{name, path}` pairs — resolves each path to an absolute, trailing-slash
 * URL (SSR falls back to the production origin) so the schema matches the canonical. Saves every
 * page from repeating the origin + toTrailingSlash boilerplate.
 */
export function breadcrumbFor(items: Array<{ name: string; path: string }>): object {
  const origin = SITE_ORIGIN;
  return breadcrumbSchema(items.map((i) => ({ name: i.name, url: origin + toTrailingSlash(i.path) })));
}

/**
 * Feature-page schema — a WebPage that points at the ONE SieveGuard application entity.
 *
 * Deliberately NOT `Product`. A feature is a credit-metered capability of SieveGuard, not a
 * separately-priced product, and `Product` without `offers` / `review` / `aggregateRating` is
 * rich-result INELIGIBLE — Search Console reports it as "Missing field offers" and no card is
 * ever shown. Inventing a price (or claiming `0`, as the genuinely-free tool pages do) would
 * contradict the pricing page, which is the structured-data/visible-content mismatch that earns
 * a manual action. `/pricing` keeps a real `Product` + `Offer` because plans DO have prices.
 *
 * `about` consolidates all six feature pages onto the single SoftwareApplication entity the
 * homepage declares, rather than minting six competing product entities that split the signal.
 */
export function featurePageSchema(opts: { name: string; description: string; path: string }): object {
  const origin = SITE_ORIGIN;
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: opts.name,
    description: opts.description,
    url: origin + toTrailingSlash(opts.path),
    // Reference the ONE WebSite / SoftwareApplication by @id (defined in baseGraph + the homepage)
    // rather than re-declaring competing copies — consolidates all feature pages onto the single
    // product entity so the signal isn't split across six lookalike nodes.
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': SOFTWARE_ID, '@type': 'SoftwareApplication', name: 'SieveGuard' },
  };
}

/**
 * ContactPage + the organisation's reachable channels.
 *
 * Why this exists: a contact page earns its SEO keep through verifiable identity, not word count
 * — reachable channels and a named legal entity are E-E-A-T signals Google applies site-wide to
 * commercial sites. This gives machines what the prose already gives humans.
 *
 * Structured data MUST mirror the visible page. When the operator flips
 * `hideBusinessIdentityInFooter` (Maltese sole trader keeping a residential address private) the
 * page drops VAT + postal address, so `vatId` is simply not passed and nothing is emitted for it.
 * The e-mail contact points are always public and monitored, so they always are — that is the
 * signal that survives without exposing an address.
 */
export function contactPageSchema(opts: {
  path: string;
  entityName: string;
  contactPoints: Array<{ contactType: string; email: string }>;
  vatId?: string;
}): object {
  const origin = SITE_ORIGIN;
  // useLegal falls back to bracketed "[Your Legal Entity Name]" / "[VAT MT…]" hints when the
  // config is unset. Those are fine as VISIBLE nudges to the operator; publishing them as
  // structured data would assert a fake company name to Google. Treat bracketed = absent.
  const real = (v: string | undefined): string | undefined =>
    v && !v.trimStart().startsWith('[') ? v : undefined;
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact',
    url: origin + toTrailingSlash(opts.path),
    mainEntity: {
      // MUST carry ORG_ID. Without it this is an ANONYMOUS second Organization —
      // same name, same url, no @id — so engines read it as a different entity to the
      // site-wide `#organization` node and the contactPoint below attaches to a fork
      // rather than to the real company. That is exactly the "lookalike copies that
      // split the signal" case docs/SEO.md exists to prevent. With the @id, this node
      // MERGES into the base-graph Organization and contributes contactPoint (and vatID)
      // to it — which is also why those properties live here and nowhere else.
      '@id': ORG_ID,
      '@type': 'Organization',
      name: real(opts.entityName) ?? 'SieveGuard',
      url: `${origin}/`,
      // Omitted entirely rather than emitted empty when identity is hidden — an empty vatID is
      // worse than none (it asserts "no VAT number" instead of staying silent).
      ...(real(opts.vatId) ? { vatID: opts.vatId } : {}),
      contactPoint: opts.contactPoints.map((cp) => ({
        '@type': 'ContactPoint',
        contactType: cp.contactType,
        email: cp.email,
        availableLanguage: 'en',
      })),
    },
  };
}

/** WebSite entity — names the site + its publisher; helps brand SERP + entity consolidation. */
export function websiteSchema(opts: { name: string; url: string }): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: opts.name,
    url: opts.url,
    publisher: { '@id': ORG_ID },
  };
}

/**
 * SoftwareApplication schema — the product entity itself, not a marketing Product. Carries the
 * stable @id so the pricing page can attach the REAL price range (an AggregateOffer built from
 * live plans) to the same entity. The homepage deliberately passes NO `offers`: a hardcoded
 * `price: 0` made every LLM read "SieveGuard is free", contradicting /pricing. Price now lives in
 * exactly one place — the pricing page's same-@id node — so the entity says "starts free, priced
 * per plan" without the homepage asserting a number.
 */
export function softwareApplicationSchema(opts: {
  name: string;
  description: string;
  url: string;
  offers?: object;
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': SOFTWARE_ID,
    name: opts.name,
    description: opts.description,
    url: opts.url,
    applicationCategory: 'SecurityApplication',
    operatingSystem: 'Web',
    publisher: { '@id': ORG_ID },
    ...(opts.offers ? { offers: opts.offers } : {}),
  };
}
